<template>
  <v-container class="my-5">
    <v-layout row wrap class="justify-center">
      <v-flex xs12>
        <v-card-title class="mb-4">
          <v-btn icon @click="$router.go(-1)" class="mr-3">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="headline font-weight-bold">Programación de Clínicas</span>
          <v-spacer></v-spacer>
          <v-btn color="primary" rounded elevation="2" @click="openDialog()">
            <v-icon left>mdi-plus</v-icon>
            Nueva Clínica
          </v-btn>
        </v-card-title>

        <!-- Información General -->
        <v-card class="mb-4">
          <v-card-text class="pa-4">
            <v-row>
              <v-col cols="12" sm="6" md="3">
                <div class="text-center">
                  <div class="display-1 font-weight-bold primary--text">{{ clinics.length }}</div>
                  <div class="caption grey--text">Clínicas Totales</div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="text-center">
                  <div class="display-1 font-weight-bold green--text">{{ upcomingCount }}</div>
                  <div class="caption grey--text">Por Venir</div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="text-center">
                  <div class="display-1 font-weight-bold orange--text">{{ totalCapacity }}</div>
                  <div class="caption grey--text">Capacidad Total</div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="text-center">
                  <div class="display-1 font-weight-bold info--text">{{ totalEnrolled }}</div>
                  <div class="caption grey--text">Alumnos Inscritos</div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Tabs para organizar vista -->
        <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="mb-4">
          <v-tab>
            <v-icon left>mdi-calendar-clock</v-icon>
            Próximas Clínicas
          </v-tab>
          <v-tab>
            <v-icon left>mdi-history</v-icon>
            Historial
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab" class="transparent">
          <!-- Tab 1: Próximas Clínicas -->
          <v-tab-item>
            <v-card class="elevation-1">
              <v-card-subtitle class="pa-4">
                <v-icon small class="mr-2">mdi-information</v-icon>
                Clínicas programadas para las próximas semanas
              </v-card-subtitle>
              <v-data-table
                :headers="headers"
                :items="upcomingClinics"
                class="elevation-0"
                :no-data-text="'Sin clínicas próximas'">

                <template v-slot:item.date="{ item }">
                  <div class="font-weight-bold">{{ formatDate(item.date) }}</div>
                  <div class="caption grey--text">{{ item.time }}</div>
                </template>

                <template v-slot:item.teacher="{ item }">
                  <div class="font-weight-bold">{{ item.teacher_name }}</div>
                </template>

                <template v-slot:item.type="{ item }">
                  <v-chip small :color="getTypeColor(item.type)">
                    {{ getTypeLabel(item.type) }}
                  </v-chip>
                </template>

                <template v-slot:item.level="{ item }">
                  <v-chip small :color="getLevelColor(item.level)">
                    {{ getLevelLabel(item.level) }}
                  </v-chip>
                </template>

                <template v-slot:item.capacity="{ item }">
                  <div class="text-center">
                    <v-progress-linear
                      :value="(item.enrolled / item.capacity) * 100"
                      height="6"
                      class="mb-1"
                    ></v-progress-linear>
                    <span class="caption">{{ item.enrolled }}/{{ item.capacity }}</span>
                  </div>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small class="mr-2" v-bind="attrs" v-on="on" @click="viewDetails(item)">
                        <v-icon small>mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    <span>Ver detalles</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small class="mr-2" v-bind="attrs" v-on="on" @click="editItem(item)">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Editar</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small v-bind="attrs" v-on="on" @click="confirmDelete(item)">
                        <v-icon small color="red">mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>Eliminar</span>
                  </v-tooltip>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>

          <!-- Tab 2: Historial -->
          <v-tab-item>
            <v-card class="elevation-1">
              <v-card-subtitle class="pa-4">
                <v-icon small class="mr-2">mdi-information</v-icon>
                Clínicas completadas
              </v-card-subtitle>
              <v-data-table
                :headers="historyHeaders"
                :items="pastClinics"
                class="elevation-0"
                :no-data-text="'Sin clínicas completadas'"
                :items-per-page="5">

                <template v-slot:item.date="{ item }">
                  <div class="font-weight-bold">{{ formatDate(item.date) }}</div>
                  <div class="caption grey--text">{{ item.time }}</div>
                </template>

                <template v-slot:item.teacher="{ item }">
                  <div class="font-weight-bold">{{ item.teacher_name }}</div>
                </template>

                <template v-slot:item.type="{ item }">
                  <v-chip small :color="getTypeColor(item.type)">
                    {{ getTypeLabel(item.type) }}
                  </v-chip>
                </template>

                <template v-slot:item.attendance="{ item }">
                  <div class="text-center">
                    <v-chip small>{{ item.enrolled }}/{{ item.capacity }}</v-chip>
                  </div>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-flex>
    </v-layout>

    <!-- Diálogo para agregar/editar clínica -->
    <ClinicDialog
      :dialog.sync="dialog"
      :clinic="editedClinic"
      :teachers="teachers"
      @save="saveClinic"
    />

    <!-- Diálogo de detalles -->
    <ClinicDetailDialog
      :dialog.sync="detailDialog"
      :clinic="selectedClinic"
      :teacher-name="selectedClinic?.teacher_name"
    />

    <!-- Diálogo de confirmación de eliminar -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">Eliminar Clínica</v-card-title>
        <v-card-text>
          ¿Estás seguro de que deseas eliminar esta clínica de <strong>{{ getTypeLabel(deleteItem?.type) }}</strong>?
          <p class="caption grey--text mt-2">
            Se desasignarán {{ deleteItem?.enrolled }} alumnos.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn color="red" text @click="deleteClinic">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { academyAPI } from '@/services/academy'
import ClinicDialog from './components/ClinicDialog.vue'
import ClinicDetailDialog from './components/ClinicDetailDialog.vue'

export default {
  name: 'AcademyClinics',
  components: {
    ClinicDialog,
    ClinicDetailDialog
  },
  data () {
    return {
      activeTab: 0,
      dialog: false,
      detailDialog: false,
      deleteDialog: false,
      deleteItem: null,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      editedClinic: null,
      selectedClinic: null,
      loading: false,
      headers: [
        { text: 'Fecha/Hora', value: 'date', width: '18%' },
        { text: 'Profesor', value: 'teacher', width: '18%' },
        { text: 'Tipo', value: 'type', width: '12%' },
        { text: 'Nivel', value: 'level', width: '12%' },
        { text: 'Inscritos', value: 'capacity', width: '15%' },
        { text: 'Acciones', value: 'actions', width: '15%', sortable: false, align: 'center' }
      ],
      historyHeaders: [
        { text: 'Fecha/Hora', value: 'date', width: '20%' },
        { text: 'Profesor', value: 'teacher', width: '20%' },
        { text: 'Tipo', value: 'type', width: '15%' },
        { text: 'Asistencia', value: 'attendance', width: '15%' }
      ],
      clinics: [],
      teachers: []
    }
  },
  computed: {
    upcomingClinics () {
      const now = new Date()
      return this.clinics
        .filter(c => this.getClinicDateTime(c) >= now)
        .sort((a, b) => this.getClinicDateTime(a) - this.getClinicDateTime(b))
    },
    pastClinics () {
      const now = new Date()
      return this.clinics
        .filter(c => this.getClinicDateTime(c) < now)
        .sort((a, b) => this.getClinicDateTime(b) - this.getClinicDateTime(a))
    },
    upcomingCount () {
      return this.upcomingClinics.length
    },
    totalCapacity () {
      return this.clinics.reduce((sum, c) => sum + c.capacity, 0)
    },
    totalEnrolled () {
      return this.clinics.reduce((sum, c) => sum + c.enrolled, 0)
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    async loadData () {
      this.loading = true
      try {
        await Promise.all([
          this.loadClinics(),
          this.loadTeachers()
        ])
      } catch (error) {
        console.error('Error loading academy clinics data:', error)
      } finally {
        this.loading = false
      }
    },
    async loadClinics () {
      try {
        const response = await academyAPI.getClinics()
        this.clinics = Array.isArray(response) ? response : response.results || []
      } catch (error) {
        console.error('Error loading clinics:', error)
        this.showSnackbar('Error al cargar clínicas', 'error')
      }
    },
    async loadTeachers () {
      try {
        const response = await academyAPI.getTeachers({ is_active: true })
        this.teachers = Array.isArray(response) ? response : response.results || []
      } catch (error) {
        console.error('Error loading teachers:', error)
      }
    },
    showSnackbar (message, color) {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.snackbar = true
    },
    formatDate (dateStr) {
      const date = new Date(dateStr)
      return date.toLocaleDateString('es-MX', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    getClinicDateTime (clinic) {
      const [year, month, day] = clinic.date.split('-').map(Number)
      const [hours, minutes] = (clinic.time || '00:00').split(':').map(Number)
      return new Date(year, month - 1, day, hours, minutes)
    },
    getTypeColor (type) {
      const colors = {
        swing: 'blue',
        putting: 'green',
        pitch: 'orange',
        full_swing: 'purple',
        short_game: 'red'
      }
      return colors[type] || 'grey'
    },
    getTypeLabel (type) {
      const labels = {
        swing: 'Swing',
        putting: 'Putting',
        pitch: 'Pitch',
        full_swing: 'Swing Completo',
        short_game: 'Juego Corto'
      }
      return labels[type] || 'Otro'
    },
    getLevelColor (level) {
      const colors = {
        basico: 'green',
        intermedio: 'orange',
        avanzado: 'red'
      }
      return colors[level] || 'grey'
    },
    getLevelLabel (level) {
      const labels = {
        basico: 'Básico',
        intermedio: 'Intermedio',
        avanzado: 'Avanzado'
      }
      return labels[level] || 'Otro'
    },
    async openDialog () {
      this.editedClinic = null
      if (this.teachers.length === 0) {
        await this.loadTeachers()
      }
      this.dialog = true
    },
    editItem (clinic) {
      this.editedClinic = { ...clinic }
      this.dialog = true
    },
    viewDetails (clinic) {
      this.selectedClinic = { ...clinic }
      this.detailDialog = true
    },
    confirmDelete (clinic) {
      this.deleteItem = clinic
      this.deleteDialog = true
    },
    async deleteClinic () {
      try {
        await academyAPI.deleteClinic(this.deleteItem.id)
        this.showSnackbar('Clínica eliminada correctamente', 'success')
        this.deleteDialog = false
        this.loadClinics()
      } catch (error) {
        console.error('Error deleting clinic:', error)
        const message = error.response?.data?.message || 'Error al eliminar la clínica'
        this.showSnackbar(message, 'error')
      }
    },
    async saveClinic (clinic) {
      try {
        if (this.editedClinic && this.editedClinic.id) {
          await academyAPI.updateClinic(this.editedClinic.id, clinic)
          this.showSnackbar('Clínica actualizada correctamente', 'success')
        } else {
          await academyAPI.createClinic(clinic)
          this.showSnackbar('Clínica programada correctamente', 'success')
        }
        this.dialog = false
        this.loadClinics()
      } catch (error) {
        console.error('Error saving clinic:', error)
        const message = error.response?.data?.message || 'Error al guardar la clínica'
        this.showSnackbar(message, 'error')
      }
    }
  }
}
</script>

<style scoped>
</style>
